/**
 * Platform — referans-parite (features/platform/types/Platform.ts). Çalışılan
 * platformu (web/iOS/Android/Electron/PWA) tespit eder ve `Platform.select`
 * ile platforma göre değer seçmeyi sağlar.
 *
 * FARK (bilinçli): kaynak-referans modül üst-düzeyinde doğrudan `navigator`/`window`
 * okur; TEBHOS'ta SSR/işçi (worker) güvenliği için `typeof` korumaları eklendi
 * (bkz. ui/utils/FocusManager deseni).
 */

const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : "";
const isIOSDevice = /iPad|iPhone|iPod/i.test(userAgent);
const isAndroidDevice = /Android/i.test(userAgent);
const isElectron =
  typeof window !== "undefined" &&
  (
    window as {
      electron?: unknown;
    }
  ).electron !== undefined;
const isIOSWeb = isIOSDevice && !isElectron;
const isPWA =
  typeof window !== "undefined" &&
  (window.matchMedia?.("(display-mode: standalone)").matches ||
    (
      navigator as {
        standalone?: boolean;
      }
    ).standalone === true);
const isMobileBrowser = isIOSDevice || isAndroidDevice;

type PlatformSelector<T> = {
  web?: T;
  ios?: T;
  android?: T;
  electron?: T;
  default?: T;
};

function selectValue<T>(options: PlatformSelector<T>): T | undefined {
  if (isElectron && options.electron !== undefined) {
    return options.electron;
  }
  if (isIOSDevice && options.ios !== undefined) {
    return options.ios;
  }
  if (isAndroidDevice && options.android !== undefined) {
    return options.android;
  }
  if (options.web !== undefined) {
    return options.web;
  }
  return options.default;
}

export const Platform = {
  OS: "web" as const,
  isWeb: true,
  isIOS: isIOSDevice,
  isAndroid: isAndroidDevice,
  isElectron,
  isIOSWeb,
  isPWA,
  isAppleDevice: isIOSDevice,
  isMobileBrowser,
  select: selectValue,
};

export function isWebPlatform(): boolean {
  return Platform.isWeb;
}

export function isElectronPlatform(): boolean {
  return Platform.isElectron;
}

/** Tarayıcının bildirdiği birincil yerel ayar (locale) tanımlayıcısı. */
export function getNativeLocaleIdentifier(): string | null {
  if (typeof navigator === "undefined") {
    return null;
  }
  const languages = navigator.languages;
  if (Array.isArray(languages) && languages.length > 0) {
    return languages[0];
  }
  return navigator.language ?? null;
}
